import { Link, useLocation } from 'react-router-dom';
import { ChevronDown } from 'lucide-react';
import { Button } from '@/components/ui';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/DropdownMenu';
import {
  getPublicOrgLessonsDropdownItems,
  type SidebarNavItem,
} from '@/components/layout/sidebar-nav-config';
import { useUnifiedConsumerNav } from '@/hooks/useUnifiedConsumerNav';
import { useOrgPaths } from '@/hooks/useOrgPaths';
import { cn } from '@/lib/utils';

type NavLinkClass = (active: boolean) => string;

interface PublicOrgConsumerNavDropdownsProps {
  navLinkClass: NavLinkClass;
  variant?: 'desktop' | 'mobile';
}

function isDropdownGroupActive(items: SidebarNavItem[], pathname: string): boolean {
  return items.some(
    (item) => pathname === item.to || (item.to !== '/' && pathname.startsWith(`${item.to}/`))
  );
}

function ConsumerNavDropdown({
  label,
  items,
  navLinkClass,
  variant = 'desktop',
}: {
  label: string;
  items: SidebarNavItem[];
  navLinkClass: NavLinkClass;
  variant?: 'desktop' | 'mobile';
}) {
  const location = useLocation();
  const active = isDropdownGroupActive(items, location.pathname);
  const isMobile = variant === 'mobile';

  if (items.length === 0) return null;

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button
          type="button"
          variant="ghost"
          size="sm"
          className={cn(
            navLinkClass(active),
            'h-auto gap-1 font-medium',
            isMobile && 'shrink-0 px-3 py-1.5 text-xs'
          )}
          aria-label={`${label} menu`}
        >
          {label}
          <ChevronDown className={cn('shrink-0 opacity-70', isMobile ? 'h-3.5 w-3.5' : 'h-4 w-4')} aria-hidden />
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="start" className="min-w-[12rem]">
        {items.map((item) => {
          const Icon = item.icon;
          const itemActive =
            location.pathname === item.to ||
            (item.to !== '/' && location.pathname.startsWith(`${item.to}/`));
          return (
            <DropdownMenuItem key={item.to} asChild className={itemActive ? 'bg-muted' : undefined}>
              <Link to={item.to} className="flex w-full items-center gap-2">
                <Icon className="h-4 w-4 shrink-0 text-muted-foreground" aria-hidden />
                {item.label}
              </Link>
            </DropdownMenuItem>
          );
        })}
      </DropdownMenuContent>
    </DropdownMenu>
  );
}

/**
 * Signed-in consumer portal links grouped under the Academy top-nav dropdown.
 */
export function PublicOrgConsumerNavDropdowns({
  navLinkClass,
  variant = 'desktop',
}: PublicOrgConsumerNavDropdownsProps) {
  const consumerNav = useUnifiedConsumerNav();
  const { p } = useOrgPaths();

  if (!consumerNav.useUnifiedNav || !p) return null;

  const navOptions = {
    hasLessonsAccess: consumerNav.hasLessonsAccess,
    hasClubMemberAccess: consumerNav.hasClubMemberAccess,
    hasMemberships: consumerNav.hasMemberships,
    hasCourtHire: consumerNav.hasCourtHire,
    hasInventoryHire: consumerNav.hasInventoryHire,
    pathFor: (subpath: string) => p(subpath) ?? subpath,
  };

  const lessonsItems = getPublicOrgLessonsDropdownItems(navOptions);

  if (lessonsItems.length === 0) return null;

  return (
    <ConsumerNavDropdown
      label="Academy"
      items={lessonsItems}
      navLinkClass={navLinkClass}
      variant={variant}
    />
  );
}
